import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { formatDateTime } from '@/lib/dates'
import type { Severity, SlaClockState, SlaClockSummary, SlaSummary } from '@/types/ticket'
import type { BusinessHours, CompanySla, HolidayRegion, SlaTarget, TargetDuration } from '@/types/sla'

dayjs.extend(relativeTime)

export const SEVERITIES: Severity[] = ['critical', 'serious', 'moderate', 'minor']
export const SLA_TARGETS: SlaTarget[] = ['response', 'resolution']

export const severityLabels: Record<Severity, string> = {
  critical: 'Critical',
  serious: 'Serious',
  moderate: 'Moderate',
  minor: 'Minor',
}

export const slaTargetLabels: Record<SlaTarget, string> = {
  response: 'Response',
  resolution: 'Resolution',
}

export const holidayLabels: Record<HolidayRegion, string> = {
  'england-and-wales': 'England & Wales',
  scotland: 'Scotland',
  'northern-ireland': 'Northern Ireland',
  none: 'None',
}

// The week as a UK reader scans it, Monday first; values are JS getDay() numbers, as the API stores them.
export const WEEKDAYS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
]

export const COMMON_TIMEZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Madrid',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
  'UTC',
]

// Mirrors the API's cap on a target's value.
export const MAX_TARGET_VALUE = 365

/** "Mon–Fri", "Mon–Wed, Fri", "Sat, Sun", or "Every day". */
export function formatWeekdays(days: number[]): string {
  if (WEEKDAYS.every(d => days.includes(d.value))) return 'Every day'
  const runs = WEEKDAYS.reduce<Array<Array<{ value: number; label: string }>>>((acc, day, i) => {
    if (!days.includes(day.value)) return acc
    const last = acc[acc.length - 1]
    if (last && last[last.length - 1] === WEEKDAYS[i - 1]) last.push(day)
    else acc.push([day])
    return acc
  }, [])
  return runs
    .map(run => (run.length > 2 ? `${run[0].label}–${run[run.length - 1].label}` : run.map(d => d.label).join(', ')))
    .join(', ')
}

/** "Mon–Fri 09:00–17:30 Europe/London" */
export function formatBusinessHours(hours: BusinessHours): string {
  return `${formatWeekdays(hours.days)} ${hours.start}–${hours.end} ${hours.timezone}`
}

/** A wall-clock span, compact: "<1m", "45m", "3h 20m", "2d 4h". */
export function formatDuration(minutes: number): string {
  const total = Math.max(0, Math.floor(minutes))
  if (total < 1) return '<1m'
  const days = Math.floor(total / 1440)
  const hours = Math.floor((total % 1440) / 60)
  const mins = total % 60
  if (days) return hours ? `${days}d ${hours}h` : `${days}d`
  if (hours) return mins ? `${hours}h ${mins}m` : `${hours}h`
  return `${mins}m`
}

const DAY_MINUTES = 24 * 60

function minutesUntil(iso: string, now: Date): number {
  return (new Date(iso).getTime() - now.getTime()) / 60_000
}

// The summary is computed when the ticket was fetched; a clock that runs out
// while the page sits open has breached even if no refetch has said so yet.
export function effectiveState(clock: SlaClockSummary, now: Date = new Date()): SlaClockState {
  const running = clock.state === 'on_track' || clock.state === 'at_risk'
  return running && clock.dueAt && minutesUntil(clock.dueAt, now) <= 0 ? 'breached' : clock.state
}

export type SlaTone = 'danger' | 'warning' | 'success' | 'neutral' | 'muted'

export interface SlaClockText {
  text: string
  /** A secondary line, e.g. when a breached clock was due. */
  detail?: string
  tone: SlaTone
}

function dueText(dueAt: string | null, now: Date): string {
  if (!dueAt) return 'Running'
  const left = minutesUntil(dueAt, now)
  return left < DAY_MINUTES ? `Due in ${formatDuration(left)}` : `Due ${formatDateTime(dueAt)}`
}

function metText(metAt: string | null, now: Date): string {
  if (!metAt) return 'Met'
  const ago = -minutesUntil(metAt, now)
  return ago < DAY_MINUTES ? `Met ${dayjs(metAt).from(now)}` : `Met ${formatDateTime(metAt)}`
}

/** What the ticket rail says about one clock. */
export function slaClockText(clock: SlaClockSummary, now: Date = new Date()): SlaClockText {
  switch (effectiveState(clock, now)) {
    case 'on_track':
      return { text: dueText(clock.dueAt, now), tone: 'neutral' }
    case 'at_risk':
      return { text: dueText(clock.dueAt, now), tone: 'warning' }
    case 'breached':
      return { text: 'Breached', detail: clock.dueAt ? `due ${formatDateTime(clock.dueAt)}` : undefined, tone: 'danger' }
    case 'met':
      return { text: metText(clock.metAt, now), tone: 'success' }
    case 'met_late':
      return { text: 'Met late', detail: clock.metAt ? formatDateTime(clock.metAt) : undefined, tone: 'warning' }
    case 'paused':
      return { text: 'Paused (on hold)', tone: 'muted' }
    case 'stopped':
      return { text: 'Not needed', tone: 'muted' }
  }
}

const URGENCY: Partial<Record<SlaClockState, number>> = { breached: 0, at_risk: 1, on_track: 2 }

/** The queue's SLA pill: the most urgent clock still running, or null when none is. */
export function slaQueuePill(summary: SlaSummary | undefined, now: Date = new Date()): { label: string; tone: SlaTone } | null {
  if (!summary) return null
  const running = SLA_TARGETS.map(target => ({ target, clock: summary[target], state: effectiveState(summary[target], now) }))
    .filter(c => URGENCY[c.state] !== undefined)
    .sort((a, b) => URGENCY[a.state]! - URGENCY[b.state]! || dueTime(a.clock) - dueTime(b.clock))
  const top = running[0]
  if (!top) return null
  const name = slaTargetLabels[top.target]
  if (top.state === 'breached') return { label: `${name} breached`, tone: 'danger' }
  const left = top.clock.dueAt ? formatDuration(minutesUntil(top.clock.dueAt, now)) : 'running'
  return { label: `${name} ${left}`, tone: top.state === 'at_risk' ? 'warning' : 'neutral' }
}

function dueTime(clock: SlaClockSummary): number {
  return clock.dueAt ? new Date(clock.dueAt).getTime() : Infinity
}

function minutesOfDay(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

function targetMinutes(duration: TargetDuration, hours: BusinessHours): number {
  // A day target is a business day, as the API counts it.
  return duration.unit === 'days' ? duration.value * (minutesOfDay(hours.end) - minutesOfDay(hours.start)) : duration.value * 60
}

export type SlaFormField = 'primaryContactId' | 'days' | 'hours' | Severity

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

/** The obvious mistakes in an SLA policy, keyed by the field to show them against. Mirrors the API's checks. */
export function slaFormErrors(sla: CompanySla): Partial<Record<SlaFormField, string>> {
  const errors: Partial<Record<SlaFormField, string>> = {}
  const { businessHours: hours } = sla
  if (sla.enabled && !sla.primaryContactId) errors.primaryContactId = 'Choose a primary contact before turning SLAs on'
  if (!hours.days.length) errors.days = 'Choose at least one working day'
  const timesValid = TIME_PATTERN.test(hours.start) && TIME_PATTERN.test(hours.end)
  if (!timesValid) errors.hours = 'Enter start and end times as HH:MM'
  else if (minutesOfDay(hours.end) <= minutesOfDay(hours.start)) errors.hours = 'End time must be after start time'

  SEVERITIES.forEach(severity => {
    const { response, resolution } = sla.targets[severity]
    const bad = SLA_TARGETS.find(t => {
      const { value } = sla.targets[severity][t]
      return !Number.isFinite(value) || value <= 0 || value > MAX_TARGET_VALUE
    })
    // The API rounds to quarters; say so here rather than save a different number from the one typed.
    const offStep = SLA_TARGETS.find(t => !Number.isInteger(sla.targets[severity][t].value * 4))
    if (bad) errors[severity] = `${slaTargetLabels[bad]} must be above 0 and at most ${MAX_TARGET_VALUE}`
    else if (offStep) errors[severity] = `${slaTargetLabels[offStep]} must be in steps of 0.25`
    else if (timesValid && targetMinutes(resolution, hours) < targetMinutes(response, hours)) {
      errors[severity] = `${severityLabels[severity]}: resolution can't be sooner than response`
    }
  })
  return errors
}
